import { createContext, type PropsWithChildren, useContext, useState } from 'react';

import type { DefaultFormType, FormContextPropsType, FormContextType } from '~/contexts/FormContext/types';
import { useFieldV2 as useField } from '~/hooks';

export const FormContext = createContext<FormContextType>({
  submitted: false,
  submitting: false,
  form: {},
  getValues: () => ({}),
  canSubmit: () => false,
  submit: () => undefined,
  forceSubmit: () => undefined,
  resetFields: () => undefined,
  resetForm: () => undefined,
});

export default function FormContextProvider<T extends DefaultFormType = DefaultFormType>({
  children,
  onSubmit,
  defaultValues,
  defaultSubmitted,
}: PropsWithChildren<FormContextPropsType<T>>) {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(defaultSubmitted ?? false);

  const form = Object.fromEntries(
    // eslint-disable-next-line react-hooks/rules-of-hooks -- hook-in-loop, but the keys of defaultValues are fixed per provider usage site and never change across renders, so hook order is stable for a given mount
    Object.entries(defaultValues).map(([field, defaultValue]) => [field, useField(field, defaultValue)]),
  );

  const getValues = () => Object.fromEntries(Object.entries(form).map(([name, field]) => [name, field.value])) as T;

  const canSubmit = () => Object.values(form).every(({ error }) => !error);

  const forceSubmit = async () => {
    setSubmitted(true);
    setSubmitting(true);
    const result = await onSubmit(getValues());
    setSubmitting(false);
    return result;
  };

  const submit = async () => {
    setSubmitted(true);
    if (canSubmit()) {
      return forceSubmit();
    }
  };

  const resetFields = (fieldsToReset?: (keyof T)[]) =>
    (fieldsToReset || Object.keys(form)).forEach(field => form[field as string]?.reset());

  const resetForm = () => {
    setSubmitted(false);
    resetFields();
  };

  return (
    <FormContext.Provider
      value={{ form, submitting, submitted, submit, forceSubmit, canSubmit, getValues, resetFields, resetForm }}
    >
      {children}
    </FormContext.Provider>
  );
}

export const useFormContext = <T extends DefaultFormType = DefaultFormType>() =>
  useContext(FormContext) as FormContextType<T>;
